import dayjs from 'dayjs'
import styles from './TimelineTabV1.module.less'
import { useCallback, useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import { commonUtils } from '@/helpers/commonUtils'
import { useDebounceFn, useEventListener } from 'ahooks'
import AIcon from '@/components/A/AIcon/AIcon'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'

interface Props {
  project: ProjectModels.ProjectDetail
  activeIndex?: number
  onFrameChange: (activeIndex: number) => void
}

/**
 * 刻度宽度，这个刻度宽度是根据设计稿来的，设计稿上刻度宽度是60px，所以这里也设置为60px
 * NOTE: 如果设计稿刻度宽度变了，这里需要同步修改
 */
const CALIBRATION_WIDTH = 60

const FRAME_WIDTH = 76

const ANIMATION_DURATION = 100

function useScrolling(element: HTMLDivElement | null) {
  const [accDelta, setAccDelta] = useState<{ value: number }>({ value: 0 })
  useEventListener(
    'wheel',
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      const event = e as WheelEvent
      const { deltaX, deltaY } = event
      const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY
      setAccDelta({ value: delta })
    },
    { target: element }
  )
  useDebounceFn(setAccDelta, { wait: 1000 })
  return { accDelta }
}

function useGenTranslateXRange(outer: HTMLDivElement | null, inner: HTMLDivElement | null) {
  const ratio = 0.35
  const DEFAULT_X_OFFSET = CALIBRATION_WIDTH * ratio * -1
  const [translateXRange, setTranslateXRange] = useState<{
    min: number
    max: number
  }>({
    /** 负数是相反的 */
    max: DEFAULT_X_OFFSET,
    min: 0,
  })

  useEffect(() => {
    if (!outer || !inner) {
      return
    }
    const { right: rightBoundary } = outer.getBoundingClientRect()
    const { right: rightBoundaryInner } = inner.getBoundingClientRect()
    setTranslateXRange((range) => {
      const min = rightBoundary - rightBoundaryInner
      return {
        max: DEFAULT_X_OFFSET,
        min: min >= 0 ? range.max : min,
      }
    })
  }, [outer, inner])
  return translateXRange
}

function TimelineTabV1(props: Props) {
  const isPortraitMode = isPortraitByProjectInfo(props.project)
  const heatmaps = props.project.detailMetrics.map((hm, index) => {
    return {
      frame: hm.heatMap,
      secondCount: index + 1,
    }
  })
  const frameInfos = heatmaps.reduce(
    (acc, frame) => {
      const info = {
        snippet: frame.frame,
        second: dayjs(Number(frame.secondCount) * 1000).format('mm:ss'),
      }
      return [...acc, info]
    },
    [{ snippet: '', second: '00:00' }]
  )

  const [outerElement, setOuterElement] = useState<HTMLDivElement | null>(null)
  const [innerElement, setInnerElement] = useState<HTMLDivElement | null>(null)
  const [frameElement, setFrameElement] = useState<HTMLDivElement | null>(null)

  const [activeIndex, setActiveIndex] = useState<{ value: number }>({ value: 1 })
  const translateXRange = useGenTranslateXRange(outerElement, innerElement)
  const [translateX, setTranslateX] = useState(-22)
  const [frameLeft, setFrameLeft] = useState(0)
  const { accDelta } = useScrolling(outerElement)
  const scrollTimeoutRef = useRef<any>()
  // const [boundary, setBoundary] = useState ... (Removed)
  const snippetRefs = useRef<(HTMLDivElement | null)[]>([])
  const lastSyncedIndexRef = useRef<number | null>(null)

  const moveLeft = () => {
    const nextIndex = activeIndex.value - 1
    if (nextIndex >= 1) {
      setActiveIndex({ value: nextIndex })
    }
  }

  const moveRight = () => {
    const nextIndex = activeIndex.value + 1
    if (nextIndex < frameInfos.length) {
      setActiveIndex({ value: nextIndex })
    }
  }

  const findTargetElement = useCallback(() => {
    if (!frameElement) {
      return { targetIndex: -1 }
    }
    const {
      left: frameLeft,
      right: frameRight,
      width: frameWidth,
    } = frameElement.getBoundingClientRect()

    let maxRatio = 0
    let targetIndex = -1

    snippetRefs.current.forEach((el, index) => {
      if (!el) return
      const { left: elLeft, right: elRight } = el.getBoundingClientRect()
      let ratio = 0
      if (elLeft < frameLeft) {
        ratio = (elRight - frameLeft) / frameWidth
      }
      if (elRight > frameRight) {
        ratio = (frameRight - elLeft) / frameWidth
      }
      if (elLeft >= frameLeft && elRight <= frameRight) {
        ratio = 1
      }
      if (ratio > maxRatio) {
        maxRatio = ratio
        targetIndex = index
      }
    })
    return { targetIndex }
  }, [frameElement])

  const handleSelectActiveFrame = (index: number) => {
    setActiveIndex({ value: index })
  }

  useEffect(() => {
    if (props?.activeIndex && props.activeIndex !== activeIndex.value) {
      setActiveIndex({ value: props.activeIndex })
    }
  }, [props?.activeIndex])

  useEffect(() => {
    props.onFrameChange(activeIndex.value)
  }, [activeIndex])

  /**
   * 核心视图同步逻辑：
   * 将指定 index 的元素移动到 Frame 下方，并尽可能居中
   */
  const syncView = useCallback(
    (index: number) => {
      if (!outerElement || !innerElement || !snippetRefs.current[index]) return

      const targetEl = snippetRefs.current[index]!
      const { left: targetLeft, width: targetWidth } = targetEl.getBoundingClientRect()
      const { left: outerLeft, width: outerWidth } = outerElement.getBoundingClientRect()

      // 1. 计算目标位置：我们希望 TargetCenter = OuterCenter
      const targetCenter = targetLeft + targetWidth / 2
      const outerCenter = outerLeft + outerWidth / 2
      const offset = outerCenter - targetCenter // 需要移动的距离 (正数表示向右移)

      // 2. 应用移动 (TranslateX)
      // 使用 currentTranslateX 闭包值计算目标值
      const potentialNewTranslateX = translateX + offset

      let newTranslateX = potentialNewTranslateX
      if (newTranslateX > translateXRange.max) newTranslateX = translateXRange.max
      if (newTranslateX < translateXRange.min) newTranslateX = translateXRange.min

      setTranslateX(newTranslateX)

      // 3. 计算 Frame 的位置 (跟随元素)
      const deltaX = newTranslateX - translateX
      const finalTargetLeft = targetLeft + deltaX

      // Frame 居中套住元素
      let newFrameLeft = finalTargetLeft + targetWidth / 2 - FRAME_WIDTH / 2 - outerLeft

      // 边界约束
      if (newFrameLeft < 0) newFrameLeft = 0
      if (newFrameLeft + FRAME_WIDTH > outerWidth) newFrameLeft = outerWidth - FRAME_WIDTH

      setFrameLeft(newFrameLeft)
    },
    [outerElement, innerElement, translateX, translateXRange]
  )

  useEffect(() => {
    // 仅当 activeIndex 实际变化时同步视图，防止滚动时的死循环
    if (activeIndex.value !== lastSyncedIndexRef.current) {
      const timer = setTimeout(() => {
        syncView(activeIndex.value)
        lastSyncedIndexRef.current = activeIndex.value
      }, 10)
      return () => clearTimeout(timer)
    }
  }, [activeIndex, syncView])

  // 滚动吸附逻辑
  useEffect(() => {
    clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => {
      const { targetIndex } = findTargetElement()
      if (targetIndex !== -1 && targetIndex !== activeIndex.value) {
        setActiveIndex({ value: targetIndex })
      } else if (targetIndex !== -1 && targetIndex === activeIndex.value) {
        // 如果 index 没变但没对齐，强制对齐
        syncView(targetIndex)
        // 更新 ref 防止再次触发上面的 Effect (虽然上面的 check 已经挡住了)
        lastSyncedIndexRef.current = targetIndex
      }
    }, 150)
  }, [translateX, findTargetElement, activeIndex, syncView]) // activeIndex 不放入依赖，避免循环？
  // 放入 activeIndex 会导致：setActiveIndex -> Effect runs -> setTimeout -> setActiveIndex...
  // 但如果 targetIndex === activeIndex.value，就不会 set。所以是安全的。

  // 处理滚轮事件
  useEffect(() => {
    if (accDelta.value === 0) {
      return
    }
    setTranslateX((x) => {
      const willTranslateX = x - accDelta.value
      if (willTranslateX >= translateXRange.max) {
        return translateXRange.max
      } else if (willTranslateX <= translateXRange.min) {
        return translateXRange.min
      } else {
        return willTranslateX
      }
    })
  }, [accDelta])

  // 移除旧的 boundary useEffect
  // useEffect(() => { ... }, [translateX, translateXRange, outerElement])

  const showLeftBtn = activeIndex.value > 1
  const showRightBtn = activeIndex.value < frameInfos.length - 1

  return (
    <div className={cls(styles.timelineTabV1, isPortraitMode && styles.portrait)}>
      {showLeftBtn && (
        <span
          className={styles.btn1}
          onClick={() => {
            moveLeft()
          }}
        >
          <AIcon type='icon-line-global_left'></AIcon>
        </span>
      )}

      {showRightBtn && (
        <span
          className={styles.btn2}
          onClick={() => {
            moveRight()
          }}
        >
          <AIcon type='icon-line-global_right'></AIcon>
        </span>
      )}
      <div
        className={cls(
          styles.timelineBox,
          { [styles.start]: !showLeftBtn },
          { [styles.end]: !showRightBtn }
        )}
        ref={(el) => {
          setOuterElement(el)
        }}
      >
        <div
          className={styles.frame}
          ref={(el) => {
            setFrameElement(el)
          }}
          style={{
            left: frameLeft,
            width: FRAME_WIDTH,
          }}
        >
          <div className={styles.frameBox}></div>
          <img
            className={styles.zhen}
            src={commonUtils.getImagePath('projectDetailPage/frame.png')}
            alt=''
          />
        </div>

        <div
          className={cls(styles.timeline)}
          ref={(el) => {
            setInnerElement(el)
          }}
          style={{
            transition: `transform ${ANIMATION_DURATION / 1000}s`,
            transform: `translateX(${translateX}px)`,
          }}
        >
          <div className={styles.secondBox}>
            {frameInfos.map((_, index) => {
              return (
                <div
                  className={cls(styles.second, { [styles.active]: activeIndex.value === index })}
                  key={index}
                >
                  {frameInfos[index]?.second}
                </div>
              )
            })}
          </div>
          <div className={styles.calibrationBox}>
            {frameInfos.map((_, index) => {
              return (
                <div className={styles.calibration} key={index}>
                  {Array.from({ length: 10 }).map((_, i) => {
                    return <div className={styles.ss} key={i}></div>
                  })}
                </div>
              )
            })}
          </div>
          <div className={styles.snippetBox}>
            {frameInfos.map((frame, index) => {
              return frame.snippet ? (
                <div
                  className={cls(styles.snippetWrapper, {
                    [styles.active]: activeIndex.value === index,
                  })}
                  key={index}
                  ref={(el) => (snippetRefs.current[index] = el)}
                >
                  <img
                    className={cls(styles.snippet)}
                    src={frame.snippet}
                    alt=''
                    onClick={() => {
                      handleSelectActiveFrame(index)
                    }}
                  />
                  <img className={styles.snippetCover} src={frame.snippet} alt='' />
                </div>
              ) : (
                <div
                  className={styles.placeholder}
                  key={index}
                  ref={(el) => (snippetRefs.current[index] = el)}
                ></div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimelineTabV1
